import { Component, effect, input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { Table, TableModule } from 'primeng/table';
import { TableReadOnlyComponent } from '../../../../core/components/table-read-only/table-read-only.component';
import { TableComponent } from '../../../../core/components/table/table.component';
import { AddRaisonAnnulationComponent } from './add-raison-annulation/add-raison-annulation.component';
import { AddConsequenceAnnulationComponent } from './add-consequence-annulation/add-consequence-annulation.component';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { RaisonAnnulation } from './raison-annulation-demande.model';
import { ConsequenceAnnulation } from './consequence-annulation-demande.model';
import { ReferentialsAnnulationDemandeService } from './referentials-annulation-demande.service';
import { InlineResponse } from '../../../../core/models/inline-response.model';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { GenericErrorResponse } from '../../../../core/models/generic-response.model';
import { StoreService } from '../../../../core/services/store.service';
import { HttpErrorResponse } from '@angular/common/http';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { provideTranslocoScope, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { FilterEventService } from "../../../../core/services/filter-event.service";

const dialogConfig = {
  modal: true,
  width: '30vw',
  height: 'auto',
  maximizable: true,
  breakpoints: {
    '1199px': '60vw', // Largeur à 60% du viewport si la fenêtre fait moins de 1199px de large
    '991px': '70vw', // Largeur à 70% du viewport si la fenêtre fait moins de 991px
    '575px': '90vw', // Largeur à 90% du viewport si la fenêtre fait moins de 575px
  },
};

@Component({
  selector: 'app-referentials-annulation-demande',
  standalone: true,
  imports: [CommonModule, TableModule, ReactiveFormsModule, ButtonModule, InputTextModule, FormsModule, ToastModule, TooltipModule, TranslocoModule],
  providers: [DialogService, MessageService, provideTranslocoScope({ scope: 'referentiels' })],
  templateUrl: './referentials-annulation-demande.component.html',
  styleUrl: './referentials-annulation-demande.component.css',
})
export class ReferentialsAnnulationDemandeComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private filterSubscription!: Subscription;
  @ViewChild('raisonImmobilisationTable') table!: Table;

  userHasReadWritePermission: boolean = false;

  raisonResult!: RaisonAnnulation[];
  consequenceResult!: ConsequenceAnnulation[];

  selectedRaisonAnnulation?: RaisonAnnulation;
  selectedConsequenceAnnulation?: ConsequenceAnnulation;

  ref: DynamicDialogRef | undefined;

  disableSubmit = false;
  errors: GenericErrorResponse[] = [];
  loading: boolean = false;

  editRowIndex: number | null = null; // Variable pour garder la ligne en cours d'édition
  editingLine: any = null;
  clonedRaisonAnnulation: { [s: string]: RaisonAnnulation } = {};
  clonedConsequenceAnnulation: { [s: string]: ConsequenceAnnulation } = {};
  raisonAnnulationForm = this.formBuilder.group({
    id: this.formBuilder.control<Number | null>(null),
    raison: ['']
  });
  consequenceAnnulationForm = this.formBuilder.group({
    id: this.formBuilder.control<Number | null>(null),
    consequence: ['']
  });

  constructor(
    private annulationDemandeService: ReferentialsAnnulationDemandeService,
    private storeService: StoreService,
    private messageService: MessageService,
    private dialogService: DialogService,
    private translocoService: TranslocoService,
    private formBuilder: FormBuilder,
    private filterEventService: FilterEventService,
  ) {
    effect(() => {
      const selectedContrat = this.storeService.selectedContract();
      this.ngOnInit();
    });
  }

  ngOnInit(): void {
    this.userHasReadWritePermission = this.storeService.hasAuthority('PARAMETRES_REFERENTIEL_RW');
    this.loadRaisonAnnulation();
    this.loadConsequenceAnnulation();
    this.filterSubscription = this.filterEventService.filterEvent$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event === 'reset') {
        this.clearFilter();
      }
    });
  }

  private clearFilter() {
    this.table.clear();
  }

  private loadRaisonAnnulation() {
    this.loading = true;
    this.annulationDemandeService
      .getRaisonAnnulation()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.raisonResult = data;
        this.loading = false;
      });
  }

  private loadConsequenceAnnulation() {
    this.loading = true;
    this.annulationDemandeService
      .getConsequenceAnnulation()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.consequenceResult = data;
        this.loading = false;
      });
  }

  // Raison Annulation
  ajouterRaisonAnnulation() {
    this.ref = this.dialogService.open(AddRaisonAnnulationComponent, {
      ...dialogConfig,
      header: this.translocoService.translate('jgrid.nav.addtitle'),
    });
    this.ref.onClose.subscribe((response) => {
      if (response) {
        this.showSuccessMessage('La ligne a été ajouté avec succès.');
        this.loadRaisonAnnulation();
      }
    });
  }

  modifierRaisonAnnulation(raisonAnnulation: RaisonAnnulation, index: number) {
    this.editRowIndex = index;
    this.editingLine = raisonAnnulation;
    this.selectedRaisonAnnulation = raisonAnnulation;
    const id = raisonAnnulation.id;
    this.raisonAnnulationForm.controls.id.patchValue(raisonAnnulation.id);
    this.raisonAnnulationForm.controls.raison.patchValue(raisonAnnulation.raison);
    this.clonedRaisonAnnulation[id.toString()] = { ...raisonAnnulation };
  }

  validerModifsRaisonAnnulation(raisonAnnulation: RaisonAnnulation) {
    this.annulationDemandeService.updateRaisonAnnulation(this.raisonAnnulationForm.value as RaisonAnnulation).subscribe({
      next: (response) => {
        this.handleInlineResponse(response);
        this.loadRaisonAnnulation();
      },
      error: (error) => {
        this.handleHttpErrorResponse(error);
      },
    });
  }

  annulerModifsRaisonAnnulation(raisonAnnulation: RaisonAnnulation, index: number) {
    this.editRowIndex = null;
    this.editingLine = null;
    const id = raisonAnnulation.id;
    this.raisonAnnulationForm.controls.id.patchValue(raisonAnnulation.id);
    this.raisonAnnulationForm.controls.raison.patchValue(raisonAnnulation.raison);
    this.raisonResult.filter(c=>c.id===id).map(raison => {
      raison = this.clonedRaisonAnnulation[id.toString()];
    });
    delete this.clonedRaisonAnnulation[id.toString()];
  }

  // Conséquence Annulation
  ajouterConsequenceAnnulation() {
    this.ref = this.dialogService.open(AddConsequenceAnnulationComponent, {
      ...dialogConfig,
      header: this.translocoService.translate('jgrid.nav.addtitle'),
    });
    this.ref.onClose.subscribe((response) => {
      if (response) {
        this.showSuccessMessage('La ligne a été ajouté avec succès.');
        this.loadConsequenceAnnulation();
      }
    });
  }

  modifierConsequenceAnnulation(consequenceAnnulation: ConsequenceAnnulation, index: number) {
    this.editRowIndex = index;
    this.editingLine = consequenceAnnulation;
    this.selectedConsequenceAnnulation = consequenceAnnulation;
    const id = consequenceAnnulation.id;
    this.consequenceAnnulationForm.controls.id.patchValue(consequenceAnnulation.id);
    this.consequenceAnnulationForm.controls.consequence.patchValue(consequenceAnnulation.consequence);
    this.clonedConsequenceAnnulation[id.toString()] = { ...consequenceAnnulation };
  }

  validerModifsConsequenceAnnulation(consequenceAnnulation: ConsequenceAnnulation) {
    this.annulationDemandeService.updateConsequenceAnnulation(this.consequenceAnnulationForm.value as ConsequenceAnnulation).subscribe({
      next: (response) => {
        this.handleInlineResponse(response);
        this.loadConsequenceAnnulation();
      },
      error: (error) => {
        this.handleHttpErrorResponse(error);
      },
    });
  }

  annulerModifsConsequenceAnnulation(consequenceAnnulation: ConsequenceAnnulation, index: number) {
    this.editRowIndex = null;
    this.editingLine = null;
    const id = consequenceAnnulation.id;
    this.consequenceAnnulationForm.controls.id.patchValue(consequenceAnnulation.id);
    this.consequenceAnnulationForm.controls.consequence.patchValue(consequenceAnnulation.consequence);
    this.consequenceResult.filter(c=>c.id===id).map(consequence => {
      consequence = this.clonedConsequenceAnnulation[id.toString()];
    });
    delete this.clonedConsequenceAnnulation[id.toString()];
  }

  onRaisonAnnulationSelect(raisonAnnulation: RaisonAnnulation) {
    this.selectedRaisonAnnulation = raisonAnnulation;
  }

  onConsequenceAnnulationSelect(consequenceAnnulation: ConsequenceAnnulation) {
    this.selectedConsequenceAnnulation = consequenceAnnulation;
  }

  private handleInlineResponse(response: InlineResponse): void {
    this.disableSubmit = false;
    if (response.success) {
      this.showSuccessMessage('La ligne a été modifiée avec succès.');
    } else {
      this.errors = response.errors;
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('error.popup.titre.defaut'),
        detail: 'Une erreur est survenue : ' + this.errors.map((e) => e.errorMsg).join(', '),
        life: 3000,
      });
    }
  }

  private handleHttpErrorResponse(error: HttpErrorResponse) {
    this.disableSubmit = false;
    this.errors = [];
    this.errors.push({
      errorInputId: error.status.toString(),
      errorCode: error.status.toString(),
      errorMsg: error.statusText,
    } as GenericErrorResponse);
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue : ' + error.statusText, life: 3000 });
  }

  showInfoMessage(infoMessage: string) {
    this.messageService.add({ severity: 'info', summary: this.translocoService.translate('info.popup.titre.defaut'), detail: infoMessage, life: 3000 });
  }

  showSuccessMessage(successMessage: string) {
    this.messageService.add({ severity: 'success', summary: this.translocoService.translate('summary.success'), detail: successMessage, life: 3000 });
  }

  showErrorMessage(errorMessage: string) {
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: errorMessage, life: 3000 });
  }

  // private checkIfRaisonSuccessfull(data: InlineResponse) {
  //   switch (data.success) {
  //     case true:
  //       console.log(data.object);
  //       this.hasError = false;
  //       delete this.errorMessage;
  //       this.raisonResult.push(data.object as RaisonAnnulation);
  //       this.loadRaisonAnnulation();
  //       break;
  //     case false:
  //       this.hasError = true;
  //       this.errorMessage = data.errors.map(err => err.errorMsg);
  //       console.log('Failed to add/save data');
  //       console.log(this.errorMessage);
  //       break;
  //   }
  // }

  // addRaisonAnnulation($event: any) {
  //   this.annulationDemandeService.addRaisonAnnulation($event.raison)
  //   .subscribe(
  //     {
  //       next: (data) => {
  //         this.checkIfRaisonSuccessfull(data);
  //       },
  //       error: (error) => {
  //         this.errorMessage = error.errors.errorMsg;
  //         console.log('error log');
  //         console.log(this.errorMessage);
  //       }
  //   });
  // }

  // private checkIfConsequenceSuccessfull(data: InlineResponse) {
  //   switch (data.success) {
  //     case true:
  //       console.log(data.object);
  //       this.hasError = false;
  //       delete this.errorMessage;
  //       this.consequenceResult.push(data.object as ConsequenceAnnulation);
  //       this.loadConsequenceAnnulation();
  //       break;
  //     case false:
  //       this.hasError = true;
  //       this.errorMessage = data.errors.map(err => err.errorMsg);
  //       console.log('Failed to add/save data');
  //       console.log(this.errorMessage);
  //       break;
  //   }
  // }

  // addConsequenceAnnulation($event: any) {
  //   this.annulationDemandeService.addConsequenceAnnulation($event.consequence)
  //   .subscribe(
  //     {
  //       next: (data) => {
  //         this.checkIfConsequenceSuccessfull(data);
  //       },
  //       error: (error) => {
  //         this.errorMessage = error.errors.errorMsg;
  //         console.log('error log');
  //         console.log(this.errorMessage);
  //       }
  //   });
  // }

  // editRaisonAnnulation(raisonAnnulation: any) {
  //   this.annulationDemandeService.updateRaisonAnnulation(raisonAnnulation)
  //   .subscribe(
  //     {
  //       next: (data) => {
  //         console.log(data);
  //       },
  //       error: (error) => {
  //         this.errorMessage = error;
  //         console.log('error log');
  //         console.log(error);
  //       }
  //   })
  // }

  // editConsequenceAnnulation(consequenceAnnulation: any) {
  //   this.annulationDemandeService.updateConsequenceAnnulation(consequenceAnnulation)
  //   .subscribe(
  //     {
  //       next: (data) => {
  //         console.log(data);
  //       },
  //       error: (error) => {
  //         this.errorMessage = error;
  //         console.log('error log');
  //         console.log(error);
  //       }
  //   })
  // }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
